<template>
  <div class="auto-dialer-wrapper">
    <!-- Auto Dialer Button with Text Inside -->
    <v-tooltip top>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          rounded
          large
          class="auto-dialer-btn"
          :class="buttonClass"
          v-bind="attrs"
          :loading="isLoading"
          v-on="on"
          @click="handleClick"
        >
          <div class="btn-content">
            <div
              class="icon-wrapper"
              :class="{
                'pulse-ring':
                  isAutoDialing && !autoDialerPaused && !autoDialerCountdown,
              }"
            >
              <!-- Countdown Ring -->
              <svg
                v-if="isAutoDialing && autoDialerCountdown > 0"
                class="countdown-ring"
                viewBox="0 0 48 48"
              >
                <circle class="countdown-ring-bg" cx="24" cy="24" r="20" />
                <circle
                  class="countdown-ring-progress"
                  cx="24"
                  cy="24"
                  r="20"
                  :style="{ strokeDashoffset: countdownProgress }"
                />
              </svg>

              <!-- Countdown number or Icon -->
              <span
                v-if="isAutoDialing && autoDialerCountdown > 0"
                class="countdown-number"
              >
                {{ autoDialerCountdown }}
              </span>
              <v-icon v-else-if="!isAutoDialing" size="24" color="white"
                >mdi-phone</v-icon
              >
              <v-icon v-else-if="autoDialerPaused" size="24" color="white"
                >mdi-phone</v-icon
              >
              <v-icon v-else size="24" color="white">mdi-stop</v-icon>
            </div>

            <!-- Text Label Inside Button -->
            <span v-if="!isAutoDialing" class="btn-text">Start Auto Dial</span>
          </div>

          <!-- Progress Badge -->
          <span v-if="isAutoDialing" class="progress-badge">
            {{ progress.current }}/{{ progress.total }}
          </span>
        </v-btn>
      </template>
      <span>{{ tooltipText }}</span>
    </v-tooltip>

    <!-- Auto Dialer Setup Dialog -->
    <v-dialog v-model="showSetupDialog" max-width="450" persistent>
      <v-card class="auto-dialer-dialog">
        <v-card-title class="primarygrad white--text">
          <v-icon left color="white">mdi-phone-outgoing</v-icon>
          Auto Dialer
          <v-spacer></v-spacer>
          <v-btn icon dark @click="showSetupDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4">
          <!-- Connection Status Warning -->
          <v-alert
            v-if="connectionStatus !== 'registered'"
            type="error"
            prominent
            border="left"
            class="mb-4"
          >
            <v-row align="center">
              <v-col class="grow">
                <div class="text-h6 mb-1">
                  <v-icon left color="error">mdi-alert-circle</v-icon>
                  SIP Dialer Not Connected
                </div>
                <div class="text-body-2">
                  Please configure and connect to your SIP server first.
                </div>
              </v-col>
              <v-col class="shrink">
                <v-btn color="error" outlined small @click="goToPBXSettings">
                  <v-icon left small>mdi-phone-voip</v-icon>
                  Configure PBX
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>

          <!-- List Selection -->
          <v-autocomplete
            v-model="selectedListId"
            :items="lists"
            item-text="name"
            item-value="id"
            label="Select List"
            outlined
            dense
            prepend-inner-icon="mdi-format-list-bulleted"
            :loading="loadingLists"
            @change="onListChange"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.name }}</v-list-item-title>
              </v-list-item-content>
            </template>
          </v-autocomplete>

          <!-- Contact Count -->
          <v-alert
            v-if="selectedListId && !loadingContacts"
            :type="contactCount > 0 ? 'info' : 'warning'"
            dense
            outlined
          >
            <v-icon left small>mdi-account-group</v-icon>
            <span v-if="contactCount > 0"
              >~{{ contactCount }} NEW contacts available (will fetch one at a
              time)</span
            >
            <span v-else
              >Contacts will be fetched from server during dialing</span
            >
          </v-alert>

          <v-progress-linear
            v-if="loadingContacts"
            indeterminate
            color="primary"
            class="mb-4"
          ></v-progress-linear>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-4">
          <v-btn text @click="showSetupDialog = false">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="primary"
            :disabled="!canStart"
            :loading="startingDialer"
            @click="startAutoDialer"
          >
            <v-icon left>mdi-play</v-icon>
            Start Calling
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Stop Confirmation Dialog -->
    <v-dialog v-model="showStopDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">Stop Auto Dialer?</v-card-title>
        <v-card-text>
          <p>Are you sure you want to stop the auto dialer?</p>
          <p class="mb-0 grey--text">
            Progress: {{ progress.current }} of {{ progress.total }} contacts
            called
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showStopDialog = false">Continue Calling</v-btn>
          <v-btn color="error" @click="stopAutoDialer">Stop</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";

export default {
  name: "AutoDialerButton",

  data() {
    return {
      showSetupDialog: false,
      showStopDialog: false,
      selectedListId: null,
      lists: [],
      contacts: [],
      loadingLists: false,
      loadingContacts: false,
      startingDialer: false,
    };
  },

  computed: {
    ...mapState("dialer", [
      "connectionStatus",
      "isAutoDialing",
      "autoDialerPaused",
      "autoDialerContacts",
      "autoDialerCurrentIndex",
      "autoDialerCountdown",
      "isInCall",
    ]),
    ...mapGetters("dialer", ["autoDialerProgress", "autoDialerStatus"]),

    progress() {
      return this.autoDialerProgress;
    },

    buttonClass() {
      if (!this.isAutoDialing) return "fab-idle";
      if (this.autoDialerCountdown > 0) return "fab-countdown";
      if (this.autoDialerPaused) return "fab-paused";
      return "fab-active";
    },

    tooltipText() {
      if (!this.isAutoDialing) return "Start Auto Dialer";
      if (this.autoDialerCountdown > 0)
        return `Next call in ${this.autoDialerCountdown}s`;
      if (this.autoDialerPaused) return "Resume Auto Dialer";
      return "Stop Auto Dialer";
    },

    isLoading() {
      return this.loadingLists || this.loadingContacts || this.startingDialer;
    },

    contactCount() {
      return this.contacts.length;
    },

    canStart() {
      // Now we can start even with 0 contacts since API will fetch them
      // Just need to be registered and have a list selected
      const result =
        this.connectionStatus === "registered" && this.selectedListId;

      if (!result) {
        console.log("[AUTO-DIALER-BTN] canStart = false because:", {
          connectionStatus: this.connectionStatus,
          isRegistered: this.connectionStatus === "registered",
          selectedListId: this.selectedListId,
          contactsLength: this.contacts.length,
        });
      }

      return result;
    },

    // Calculate the stroke-dashoffset for the countdown ring animation
    countdownProgress() {
      const circumference = 2 * Math.PI * 20; // r=20
      const progress = (this.autoDialerCountdown / 5) * circumference;
      return circumference - progress;
    },
  },

  watch: {
    // Watch connection status changes
    connectionStatus: {
      handler(newStatus, oldStatus) {
        console.log("[AUTO-DIALER-BTN] Connection status changed:", {
          from: oldStatus,
          to: newStatus,
          isRegistered: newStatus === "registered",
        });
      },
      immediate: true,
    },

    // Watch for disposition dialog closing to trigger next call
    "$store.state.dialer.showDispositionDialog"(showing, wasShowing) {
      // Only trigger when dialog closes (was showing, now not showing)
      if (
        wasShowing &&
        !showing &&
        this.isAutoDialing &&
        !this.autoDialerPaused
      ) {
        // Disposition was submitted, trigger next call
        console.log("[AUTO-DIALER WATCH] Disposition closed, triggering next");
        this.$store.dispatch("dialer/autoDialerAfterDisposition");
      }
    },
  },

  mounted() {
    console.log("[AUTO-DIALER-BTN] Component mounted with initial state:", {
      connectionStatus: this.connectionStatus,
      isAutoDialing: this.isAutoDialing,
      autoDialerPaused: this.autoDialerPaused,
      selectedList: this.$store.state.selectedList,
      route: this.$route.name,
    });
    this.fetchLists();
  },

  methods: {
    async handleClick() {
      console.log("[AUTO-DIALER-BTN] handleClick called", {
        isAutoDialing: this.isAutoDialing,
        connectionStatus: this.connectionStatus,
        autoDialerPaused: this.autoDialerPaused,
      });

      if (!this.isAutoDialing) {
        // Check connection status FIRST - before any other action
        console.log(
          "[AUTO-DIALER-BTN] Checking connection status:",
          this.connectionStatus,
        );

        if (this.connectionStatus !== "registered") {
          console.log(
            "[AUTO-DIALER-BTN] ❌ Connection check FAILED - showing alert",
          );
          this.$swal({
            type: "error",
            title: "SIP Dialer Not Connected",
            html: `
              <p style="margin-bottom: 16px;">You need to connect to the SIP server before using Auto Dialer.</p>
              <p style="margin-bottom: 8px;"><strong>To get started:</strong></p>
              <ol style="text-align: left; padding-left: 20px; margin: 0;">
                <li>Configure your PBX/SIP settings</li>
                <li>Connect to the SIP server</li>
                <li>Return here to start auto-dialing</li>
              </ol>
            `,
            showCancelButton: true,
            confirmButtonText:
              '<i class="mdi mdi-phone-voip"></i> Go to PBX Settings',
            cancelButtonText: "Cancel",
            confirmButtonColor: "#4CAF50",
            cancelButtonColor: "#757575",
            customClass: {
              popup: "swal-wide",
              confirmButton: "swal-btn-large",
            },
          }).then((result) => {
            if (result.value) {
              // Redirect to PBX page
              this.$router.push({ name: "GoDial PBX" });
            }
          });
          return;
        }

        console.log(
          "[AUTO-DIALER-BTN] ✅ Connection check PASSED - proceeding",
        );

        // Check if we're on CRM page and have a selected list
        const isCrmPage =
          this.$route.path === "/lists" || this.$route.name === "CRM";
        console.log("[AUTO-DIALER-BTN] Page check:", {
          isCrmPage,
          path: this.$route.path,
          name: this.$route.name,
        });
        const storeSelectedList = this.$store.state.selectedList;
        console.log(
          "[AUTO-DIALER-BTN] Store selected list:",
          storeSelectedList,
        );

        if (
          isCrmPage &&
          storeSelectedList &&
          storeSelectedList !== "" &&
          storeSelectedList !== "allLists"
        ) {
          console.log(
            "[AUTO-DIALER-BTN] 📋 Using pre-selected list from CRM page",
          );
          // On CRM page with a list already selected - use it directly
          this.selectedListId = storeSelectedList;

          // Fetch contacts count for display (API will actually fetch during dialing)
          console.log(
            "[AUTO-DIALER-BTN] Fetching contacts count for list:",
            this.selectedListId,
          );
          await this.onListChange();

          console.log(
            "[AUTO-DIALER-BTN] Contacts count loaded:",
            this.contacts.length,
          );

          // Auto-start the dialer - API will fetch contacts one at a time
          // Even if contacts.length is 0, the API might still have contacts
          console.log(
            "[AUTO-DIALER-BTN] 🚀 Auto-starting dialer (API will fetch contacts)",
          );
          this.startAutoDialer();
        } else {
          console.log(
            "[AUTO-DIALER-BTN] 📂 Showing setup dialog for list selection",
          );
          // Show setup dialog for list selection
          this.showSetupDialog = true;
          this.fetchLists();
          // Auto-refresh contacts if a list was already selected
          if (this.selectedListId) {
            this.onListChange();
          }
        }
      } else if (this.autoDialerPaused) {
        // Resume auto dialing
        this.$store.dispatch("dialer/resumeAutoDialer");
      } else {
        // Stop auto dialer immediately without confirmation
        this.stopAutoDialer();
      }
    },

    async fetchLists() {
      this.loadingLists = true;
      try {
        const teamIds = this.getTeamIds();

        if (teamIds.length === 0) {
          console.warn("[AUTO-DIALER] No team IDs found");
          this.lists = [];
          return;
        }

        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/lists/fetch`,
          { teams: teamIds },
        );

        // Filter out 'allLists' option - we need specific lists
        this.lists = (response.body || []).filter(
          (list) => list.id !== "allLists",
        );
        console.log(`[AUTO-DIALER] Loaded ${this.lists.length} lists`);
      } catch (error) {
        console.error("Failed to fetch lists:", error);
        this.lists = [];
      } finally {
        this.loadingLists = false;
      }
    },

    getTeamIds() {
      const user = this.$store.state.user;
      if (!user) return [];

      // Get selected team from store if available
      const selectedTeam = this.$store.state.selectedTeam;

      if (selectedTeam && selectedTeam !== "allTeams") {
        return [selectedTeam];
      }

      // Fall back to user's teams based on role
      if (user.role === "Manager") {
        return user.teams || [];
      } else if (user.role === "TeamManager") {
        return user.managerTeams || [];
      }

      return user.teams || [];
    },

    async onListChange() {
      if (!this.selectedListId) {
        this.contacts = [];
        return;
      }

      // Update the CRM to show the selected list
      this.$store.commit("setSelectedList", this.selectedListId);

      this.loadingContacts = true;
      this.contacts = [];

      try {
        // Use the same API pattern as Lists.vue - byFilterV4
        // Filter for NEW disposition only (contacts eligible for calling)
        const response = await this.$http.post(
          `${this.$store.state.ENDPOINT}/contacts/byFilterV4`,
          {
            queryCase: "data",
            predicates: [],
            matchType: "and",
            lists: [this.selectedListId],
            dispos: ["NEW"], // Only NEW disposition contacts
            limit: 500,
            skip: 0,
            sortField: "",
            sortOrder: "asc",
          },
        );

        const contactList = response.body || [];

        // Additional filter to ensure only NEW or empty dispo contacts
        const newContacts = contactList.filter(
          (c) => !c.dispo || c.dispo === "" || c.dispo === "NEW",
        );

        this.contacts = newContacts.map((c) => ({
          id: c.id,
          phone: c.phone,
          name: c.name || "",
          listId: c.listId,
        }));

        console.log(
          `[AUTO-DIALER] Loaded ${this.contacts.length} contacts for list ${this.selectedListId}`,
        );
      } catch (error) {
        console.error("Failed to fetch contacts:", error);
        this.contacts = [];
      } finally {
        this.loadingContacts = false;
      }
    },

    // Refresh contacts for the currently selected list
    refreshContacts() {
      if (this.selectedListId) {
        this.onListChange();
      }
    },

    async startAutoDialer() {
      console.log("[AUTO-DIALER-BTN] startAutoDialer called", {
        canStart: this.canStart,
        connectionStatus: this.connectionStatus,
        selectedListId: this.selectedListId,
        contactsCount: this.contacts.length,
      });

      // Check PBX trial call limit before starting
      try {
        const companyDetails = this.$store.getters.companyDetails;
        if (
          companyDetails &&
          companyDetails.plan &&
          companyDetails.plan.name === "Trial"
        ) {
          const response = await this.$http.get(
            `${this.$store.state.ENDPOINT}/eventMappingCaches/getPbxTrialCallCount?companyId=${companyDetails.id}`,
          );
          const data = response.body;
          if (data.isPbxCallLimitReached) {
            this.$swal({
              title: "PBX Trial Limit Reached",
              text:
                data.message ||
                "Your PBX trial call limit has been reached. Please subscribe to continue using PBX calling.",
              type: "warning",
              confirmButtonText: "Subscribe Now",
              showCancelButton: true,
              cancelButtonText: "Cancel",
            }).then((result) => {
              if (result.value) {
                this.$router.push("/payments");
              }
            });
            return;
          }
        }
      } catch (limitErr) {
        console.error(
          "[AUTO-DIALER-BTN] Error checking PBX trial limit:",
          limitErr,
        );
      }

      // Double-check connection status before starting
      console.log(
        "[AUTO-DIALER-BTN] Double-checking connection status:",
        this.connectionStatus,
      );

      if (this.connectionStatus !== "registered") {
        console.log(
          "[AUTO-DIALER-BTN] ❌ Connection check FAILED in startAutoDialer - showing alert",
        );
        this.$swal({
          type: "error",
          title: "SIP Dialer Not Connected",
          html: `
            <p style="margin-bottom: 16px;">You need to connect to the SIP server before using Auto Dialer.</p>
            <p style="margin-bottom: 8px;"><strong>To get started:</strong></p>
            <ol style="text-align: left; padding-left: 20px; margin: 0;">
              <li>Configure your PBX/SIP settings</li>
              <li>Connect to the SIP server</li>
              <li>Return here to start auto-dialing</li>
            </ol>
          `,
          showCancelButton: true,
          confirmButtonText:
            '<i class="mdi mdi-phone-voip"></i> Go to PBX Settings',
          cancelButtonText: "Cancel",
          confirmButtonColor: "#4CAF50",
          cancelButtonColor: "#757575",
          customClass: {
            popup: "swal-wide",
            confirmButton: "swal-btn-large",
          },
        }).then((result) => {
          if (result.value) {
            this.$router.push({ name: "PBX" });
          }
        });
        return;
      }

      console.log(
        "[AUTO-DIALER-BTN] ✅ Connection check PASSED in startAutoDialer",
      );

      if (!this.canStart) {
        console.log("[AUTO-DIALER-BTN] ⚠️ Cannot start - canStart is false");
        return;
      }

      console.log(
        "[AUTO-DIALER-BTN] 🎯 Dispatching startAutoDialer action to store...",
      );
      this.startingDialer = true;
      try {
        await this.$store.dispatch("dialer/startAutoDialer", {
          contacts: this.contacts,
          listId: this.selectedListId,
        });

        console.log("[AUTO-DIALER-BTN] ✅ Auto dialer started successfully");
        this.showSetupDialog = false;
      } catch (error) {
        console.error(
          "[AUTO-DIALER-BTN] ❌ Failed to start auto dialer:",
          error,
        );
        this.$swal({
          type: "error",
          title: "Failed to Start",
          text: error.message || "Could not start auto dialer",
        });
      } finally {
        this.startingDialer = false;
      }
    },

    async stopAutoDialer() {
      await this.$store.dispatch("dialer/stopAutoDialer");
      this.showStopDialog = false;

      // Show confirmation
      this.$swal({
        type: "success",
        title: "Auto Dialer Stopped",
        text: "Auto dialing has been stopped successfully.",
        timer: 2000,
        showConfirmButton: false,
      });
    },

    // Navigate to PBX settings page
    goToPBXSettings() {
      this.showSetupDialog = false;
      this.$router.push({ name: "GoDial PBX" });
    },

    // Method to check if auto-dialer is running
    canLeave() {
      if (this.isAutoDialing) {
        return window.confirm(
          "Auto Dialer is currently running. Are you sure you want to stop it and leave?",
        );
      }
      return true;
    },
  },
};
</script>

<style scoped>
.auto-dialer-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Auto Dialer Button with Text Inside */
.auto-dialer-btn {
  height: 56px !important;
  padding: 0 1rem 0 0.4rem !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative;
  overflow: visible !important;
  text-transform: none !important;
  letter-spacing: 0.5px !important;
}

/* Button Content Container */
.btn-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Button Text */
.btn-text {
  font-size: 15px;
  font-weight: 600;
  color: white;
  white-space: nowrap;
  letter-spacing: 0.3px;
}

/* Icon Wrapper */
.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  position: relative;
}

/* Auto Dialer Button States */
.auto-dialer-btn.fab-idle {
  background: linear-gradient(145deg, #4caf50 0%, #43a047 100%) !important;
  box-shadow: 0 4px 14px rgba(76, 175, 80, 0.35), 0 2px 6px rgba(0, 0, 0, 0.1) !important;
}

.auto-dialer-btn.fab-idle:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px rgba(76, 175, 80, 0.45), 0 3px 8px rgba(0, 0, 0, 0.12) !important;
}

.auto-dialer-btn.fab-active {
  background: linear-gradient(145deg, #66bb6a 0%, #4caf50 100%) !important;
  box-shadow: 0 4px 14px rgba(76, 175, 80, 0.4), 0 2px 6px rgba(0, 0, 0, 0.1) !important;
}

.auto-dialer-btn.fab-countdown {
  background: linear-gradient(145deg, #ffa726 0%, #ff9800 100%) !important;
  box-shadow: 0 4px 14px rgba(255, 152, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.1) !important;
}

.auto-dialer-btn.fab-paused {
  background: linear-gradient(145deg, #ffb74d 0%, #ffa726 100%) !important;
  box-shadow: 0 4px 14px rgba(255, 152, 0, 0.35), 0 2px 6px rgba(0, 0, 0, 0.1) !important;
}

/* Inner icon container - keep for backward compatibility */
.fab-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  position: relative;
  z-index: 1;
}

.fab-inner .v-icon {
  color: white !important;
}

/* Pulse ring animation for active calling */
.pulse-ring::before,
.pulse-ring::after {
  content: "";
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.6);
  opacity: 0;
  animation: pulse-ring 2s ease-out infinite;
}

.pulse-ring::after {
  animation-delay: 1s;
}

@keyframes pulse-ring {
  0% {
    transform: scale(0.8);
    opacity: 0.8;
  }
  100% {
    transform: scale(1.8);
    opacity: 0;
  }
}

/* Countdown Ring */
.countdown-ring {
  position: absolute;
  width: 48px;
  height: 48px;
  transform: rotate(-90deg);
  z-index: 0;
}

.countdown-ring-bg {
  fill: none;
  stroke: rgba(255, 255, 255, 0.25);
  stroke-width: 3;
}

.countdown-ring-progress {
  fill: none;
  stroke: rgba(255, 255, 255, 0.9);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 125.66; /* 2 * PI * 20 */
  transition: stroke-dashoffset 0.3s ease;
}

/* Countdown Number */
.countdown-number {
  font-size: 18px;
  font-weight: 700;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  z-index: 2;
}

/* Progress Badge */
.progress-badge {
  position: absolute;
  top: -4px;
  right: -8px;
  background: linear-gradient(145deg, #1976d2, #1565c0);
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 10px;
  min-width: 32px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(25, 118, 210, 0.4);
  z-index: 3;
  border: 1.5px solid white;
}

.auto-dialer-dialog {
  border-radius: 16px;
  overflow: hidden;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .toolbar-fab {
    width: 50px !important;
    height: 50px !important;
  }

  .countdown-ring {
    width: 42px;
    height: 42px;
  }

  .countdown-number {
    font-size: 16px;
  }

  .progress-badge {
    font-size: 8px;
    padding: 1px 5px;
    min-width: 28px;
  }
}

@media (max-width: 360px) {
  .toolbar-fab {
    width: 46px !important;
    height: 46px !important;
  }
}

/* Auto Dial Icon - PNG Image */
.auto-dial-img {
  width: 40px;
  height: auto;
  object-fit: contain;
}
</style>
